import { useMemo, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw, Send } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { CalendarioRegistro } from '../componentes/CalendarioRegistro'
import { LegendaCores } from '../componentes/LegendaCores'
import { useCalendario, useRegistrarHoje, useSolicitarAjuste } from '../hooks/useAjudaCusto'
import { CalendarioDia } from '../tipos/ajudaCusto'
import { NOMES_MESES, formatarData } from '../utils'

export default function Registro() {
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)

  const { data: calendario, isLoading, refetch, isFetching } = useCalendario(ano, mes)
  const registrarHoje = useRegistrarHoje()
  const solicitarAjuste = useSolicitarAjuste()

  const [diaSelecionado, setDiaSelecionado] = useState<CalendarioDia | null>(null)
  const [justificativa, setJustificativa] = useState('')

  const dias = useMemo(() => calendario?.dias ?? [], [calendario])

  const irMes = (delta: number) => {
    let novoMes = mes + delta
    let novoAno = ano
    if (novoMes < 1) {
      novoMes = 12
      novoAno -= 1
    } else if (novoMes > 12) {
      novoMes = 1
      novoAno += 1
    }
    setMes(novoMes)
    setAno(novoAno)
  }

  const handleClickDia = (dia: CalendarioDia) => {
    if (dia.podeRegistrarHoje) {
      registrarHoje.mutate()
      return
    }
    if (dia.podeSolicitarAjuste) {
      setDiaSelecionado(dia)
      setJustificativa('')
    }
  }

  const confirmarSolicitacao = async () => {
    if (!diaSelecionado || !justificativa.trim()) return
    await solicitarAjuste.mutateAsync({ data: diaSelecionado.data, justificativa: justificativa.trim() })
    setDiaSelecionado(null)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bônus Presencial — Registro</h1>
          <p className="text-gray-600">Marque sua frequência clicando no dia de hoje.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 p-6 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-brand-500 to-brand-600">
              <CalendarDays className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-800">
              {NOMES_MESES[mes - 1]} {ano}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={() => irMes(-1)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => irMes(1)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-gray-500">Carregando calendário...</div>
        ) : (
          <CalendarioRegistro dias={dias} onClickDia={handleClickDia} />
        )}

        <div className="mt-6 border-t border-gray-100 pt-4">
          <LegendaCores />
        </div>
      </div>

      <Modal
        isOpen={!!diaSelecionado}
        onClose={() => setDiaSelecionado(null)}
        title="Solicitar ajuste de registro"
        subtitle={diaSelecionado ? formatarData(diaSelecionado.data) : undefined}
        icon={<Send className="h-6 w-6 text-brand-500" />}
        size="md"
        footer={
          <div className="flex justify-end gap-3 p-4">
            <button
              type="button"
              onClick={() => setDiaSelecionado(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={!justificativa.trim() || solicitarAjuste.isLoading}
              onClick={confirmarSolicitacao}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
            >
              Confirmar solicitação
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Descreva a justificativa para o ajuste deste dia. Os administradores serão notificados para aprovação.
          </p>
          <textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            rows={4}
            placeholder="Justificativa..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </Modal>
    </div>
  )
}
